import React, { useCallback, useState, useEffect, useReducer, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { initialState, reducer } from '../reducers/propertyUserGroups/containerReducer';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { useValidation, isValid } from '@totalsoft/pure-validations-react';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { propertyUserGroupsValidator } from 'features/properties/components/edit/validators';
import { USER_GROUPS_QUERY } from 'features/common/components/userGroup/userGroupList/queries';
import { ADD_UPDATE_PROPERTY_USER_GROUPS } from '../../mutations';
import { PROPERTY_USER_GROUPS_UPDATED } from '../../subscriptions';
import PropertyUserGroupsListComponent from './PropertyUserGroupsListComponent';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useApolloClient } from '@apollo/client';

const PropertyUserGroupsListContainer = ({ propertyId, data }) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const client = useApolloClient();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const [validation, validate] = useValidation(propertyUserGroupsValidator);

  const isNewProperty = useMemo(() => !propertyId, [propertyId]);

  const { data: userGroupsData, refetch } = useQuery(USER_GROUPS_QUERY);

  const [addOrUpdatePropertyUserGroups] = useMutation(ADD_UPDATE_PROPERTY_USER_GROUPS);

  useSubscription(PROPERTY_USER_GROUPS_UPDATED, {
    onData: ({ data: { data } }) => {
      dispatch({ type: 'OnPropertyUserGroupUpdated' });

      client.cache.evict({ id: `Property:{"id":${data.propertyUserGroupsUpdated.propertyId}}` });
      client.cache.gc();
      addToast(t('Property.PropertyUserGroupsUpdated'), 'success');
    }
  });

  const handleAdd = () => {
    dispatch({
      type: 'OnPropertyUserGroupAdded',
      propertyId
    });
  };

  const handlePropertyChanged = (propName, propValue, index) => {
    dispatch({ type: 'OnPropertyUserGroupChanged', propName, propValue, index });
  };

  const handleDelete = useCallback(
    index => {
      confirm(
        () => {
          dispatch({ type: 'OnPropertyUserGroupDelete', index });
        },
        {
          title: t('Property.DeleteUserGroup'),
          content: t('Property.AreYouSureDeleteUserGroup')
        }
      );
    },
    [confirm, t]
  );

  const handleCancel = index => {
    dispatch({ type: 'OnCancelPropertyUserGroup', index });
  };

  const handleEdit = index => {
    dispatch({ type: 'OnEditPropertyUserGroup', index });
  };

  const handleSave = () => {
    const input = {
      // eslint-disable-next-line no-unused-vars
      propertyUserGroups: localState.values.map(({ inEdit, editValue, ...x }) => ({
        ...x,
        ...editValue
      })),
      propertyId: propertyId
    };
    setSaveDisabled(true);
    return addOrUpdatePropertyUserGroups({ variables: { input } });
  };

  const onLanguageChange = useCallback(() => {
    refetch();
  }, [refetch]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  useEffect(() => {
    validate(localState.values);
  }, [localState, validate]);

  useEffect(() => {
    setSaveDisabled(!(validation |> isValid));
  }, [localState, validation]);

  return (
    <PropertyUserGroupsListComponent
      propertyUserGroups={localState}
      userGroups={userGroupsData ? userGroupsData.userGroupsConfig : []}
      onPropertyChanged={handlePropertyChanged}
      onSave={handleSave}
      onDelete={handleDelete}
      onAdd={handleAdd}
      onCancel={handleCancel}
      onEdit={handleEdit}
      validation={validation}
      saveDisabled={saveDisabled}
      changed={localState.changed}
      isNewProperty={isNewProperty}
    />
  );
};

PropertyUserGroupsListContainer.propTypes = {
  propertyId: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired
};

export default PropertyUserGroupsListContainer;
